import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Cpu, Shield, Link2, Smartphone, Search, Users, BarChart3,
  CheckCircle, XCircle, Clock, Save, TrendingUp, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { getClients } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const StatusBadge = ({ active, label }) => (
  <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${active ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
    {active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </div>
);

export default function ClientAuditPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getClients();
        setClients(res.data);
        if (res.data.length > 0) {
          setSelectedClient(res.data[0].id);
          // Load audit for first client
          const auditRes = await axios.get(`${BACKEND_URL}/api/clients/${res.data[0].id}/audit`);
          setAudit(auditRes.data);
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      const fetchAudit = async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/clients/${selectedClient}/audit`);
          setAudit(res.data);
        } catch (err) { console.error(err); }
      };
      fetchAudit();
    }
  }, [selectedClient]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/clients/${selectedClient}/audit`, audit);
      toast.success('Audit-Daten gespeichert');
    } catch (err) { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };

  const updateField = (field, value) => setAudit(prev => ({ ...prev, [field]: value }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-sm text-slate-500">Laden...</div></div>;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.04 } } }}>
      <PageHeader title="Client Audit & Tracking" description="Technischer Status, ASO-Score und Retention-Metriken pro Kunde">
        <Select value={selectedClient} onValueChange={setSelectedClient}>
          <SelectTrigger className="w-52" data-testid="audit-client-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
          <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={handleSave} disabled={saving} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="audit-save-button">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Speichern...' : 'Speichern'}
        </Button>
      </PageHeader>

      {audit && (
        <>
          {/* Tech & Tracking Status */}
          <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Cpu className="w-5 h-5 text-sky-600" />
              <h3 className="font-display text-base font-semibold text-slate-900">Tech & Tracking</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Tracking-Status</label>
                <Select value={audit.tracking_status || 'not_started'} onValueChange={v => updateField('tracking_status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Nicht gestartet</SelectItem>
                    <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                    <SelectItem value="complete">Komplett</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">MMP-Provider</label>
                <Select value={audit.mmp_provider || 'none'} onValueChange={v => updateField('mmp_provider', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein MMP</SelectItem>
                    <SelectItem value="appsflyer">AppsFlyer</SelectItem>
                    <SelectItem value="adjust">Adjust</SelectItem>
                    <SelectItem value="branch">Branch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => updateField('skan_configured', !audit.skan_configured)} className="cursor-pointer">
                <StatusBadge active={audit.skan_configured} label="SKAN 4.0" />
              </button>
              <button onClick={() => updateField('deep_links_active', !audit.deep_links_active)} className="cursor-pointer">
                <StatusBadge active={audit.deep_links_active} label="Deep-Links" />
              </button>
              <button onClick={() => updateField('sdk_integrated', !audit.sdk_integrated)} className="cursor-pointer">
                <StatusBadge active={audit.sdk_integrated} label="SDK Integration" />
              </button>
            </div>
          </motion.div>

          {/* ASO */}
          <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-teal-600" />
              <h3 className="font-display text-base font-semibold text-slate-900">ASO (App Store Optimization)</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">ASO-Score (0-100)</label>
                <Input type="number" min="0" max="100" value={audit.aso_score || 0} onChange={e => updateField('aso_score', parseInt(e.target.value) || 0)} />
                <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${audit.aso_score >= 70 ? 'bg-emerald-500' : audit.aso_score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${audit.aso_score || 0}%` }} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Keywords getrackt</label>
                <Input type="number" value={audit.aso_keywords_count || 0} onChange={e => updateField('aso_keywords_count', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Paywall-Conversion (%)</label>
                <Input type="number" step="0.1" value={audit.paywall_conversion || 0} onChange={e => updateField('paywall_conversion', parseFloat(e.target.value) || 0)} />
              </div>
            </div>
          </motion.div>

          {/* Retention & Engagement */}
          <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-500" />
              <h3 className="font-display text-base font-semibold text-slate-900">Retention & Engagement</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">D1 Retention (%)</label>
                <Input type="number" step="0.1" value={audit.retention_d1 || 0} onChange={e => updateField('retention_d1', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">D7 Retention (%)</label>
                <Input type="number" step="0.1" value={audit.retention_d7 || 0} onChange={e => updateField('retention_d7', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">D30 Retention (%)</label>
                <Input type="number" step="0.1" value={audit.retention_d30 || 0} onChange={e => updateField('retention_d30', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">LTV 30d (€)</label>
                <Input type="number" step="0.01" value={audit.ltv_30d || 0} onChange={e => updateField('ltv_30d', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">LTV 90d (€)</label>
                <Input type="number" step="0.01" value={audit.ltv_90d || 0} onChange={e => updateField('ltv_90d', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">ARPU (€)</label>
                <Input type="number" step="0.01" value={audit.arpu || 0} onChange={e => updateField('arpu', parseFloat(e.target.value) || 0)} className="text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">DAU (Daily Active Users)</label>
                <Input type="number" value={audit.dau || 0} onChange={e => updateField('dau', parseInt(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">MAU (Monthly Active Users)</label>
                <Input type="number" value={audit.mau || 0} onChange={e => updateField('mau', parseInt(e.target.value) || 0)} className="text-sm" />
              </div>
            </div>
          </motion.div>

          {/* KPI Summary Cards */}
          <motion.div variants={fadeIn} className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="D1 Retention" value={audit.retention_d1 || 0} suffix="%" testId="audit-d1" />
            <KpiCard label="D7 Retention" value={audit.retention_d7 || 0} suffix="%" testId="audit-d7" />
            <KpiCard label="LTV 90d" value={audit.ltv_90d || 0} prefix="€" testId="audit-ltv" />
            <KpiCard label="DAU/MAU" value={audit.mau > 0 ? Math.round((audit.dau / audit.mau) * 100) : 0} suffix="%" testId="audit-stickiness" />
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
